/**
 * WASM 执行模块
 * 允许客户端动态加载和运行 WebAssembly handler
 */

/// <reference types="node" />
declare const WebAssembly: any

/** WASM 模块配置 */
export interface WasmModuleConfig {
  /** 模块名称 */
  name: string
  /** WASM 二进制数据 */
  binary: Buffer | Uint8Array
  /** 导入函数映射 */
  imports?: Record<string, Function>
  /** 内存限制 (pages, 每页 64KB) */
  memoryPages?: number
  /** 执行超时 (ms) */
  timeout?: number
}

/** WASM 模块实例 */
export interface WasmInstance {
  name: string
  exports: Record<string, Function>
  memory: any
  loadedAt: number
}

/**
 * WASM Runner
 * 管理 WASM 模块的加载、执行和卸载
 */
export class WasmRunner {
  private modules = new Map<string, WasmInstance>()
  private maxModules: number

  constructor(options?: { maxModules?: number }) {
    this.maxModules = options?.maxModules || 10
  }

  /** 加载 WASM 模块 */
  async load(config: WasmModuleConfig): Promise<WasmInstance> {
    if (this.modules.size >= this.maxModules) {
      throw new Error(`WASM module limit reached (${this.maxModules})`)
    }
    if (this.modules.has(config.name)) {
      throw new Error(`Module '${config.name}' already loaded`)
    }

    const memory = new WebAssembly.Memory({
      initial: config.memoryPages || 1,
      maximum: config.memoryPages ? config.memoryPages * 2 : 10,
    })

    const importObject: any = {
      env: {
        memory,
        abort: () => { throw new Error('WASM abort') },
        ...config.imports,
      },
    }

    const module = await WebAssembly.compile(config.binary)
    const instance = await WebAssembly.instantiate(module, importObject)

    const wasmInstance: WasmInstance = {
      name: config.name,
      exports: instance.exports as Record<string, Function>,
      memory,
      loadedAt: Date.now(),
    }

    this.modules.set(config.name, wasmInstance)
    return wasmInstance
  }

  /** 执行 WASM 模块的导出函数 */
  async execute(moduleName: string, funcName: string, ...args: any[]): Promise<any> {
    const instance = this.modules.get(moduleName)
    if (!instance) throw new Error(`Module '${moduleName}' not loaded`)

    const fn = instance.exports[funcName]
    if (typeof fn !== 'function') {
      throw new Error(`Function '${funcName}' not found in module '${moduleName}'`)
    }

    return fn(...args)
  }

  /** 卸载模块 */
  unload(name: string): boolean {
    return this.modules.delete(name)
  }

  /** 获取已加载模块列表 */
  listModules(): { name: string; loadedAt: number }[] {
    return [...this.modules.values()].map((m) => ({
      name: m.name,
      loadedAt: m.loadedAt,
    }))
  }

  /** 检查模块是否已加载 */
  has(name: string): boolean {
    return this.modules.has(name)
  }

  /** 清空所有模块 */
  clear(): void {
    this.modules.clear()
  }

  /** 获取当前模块数量 */
  get size(): number {
    return this.modules.size
  }
}
